import React, { FC } from "react";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import { PurchaseStore } from "../store";
import {
  CheckOutRecommendedItemDetails,
  CustomerFacingCheckOutItemModel,
  CustomOptionModel,
} from "../models";
import { ICheckOutCombinedColors } from "../interfaces";
import { CheckOutButtonStyle, CheckOutRedirectTypes } from "../enums";
import { MainOuterCard, MultiplePriceWrapper } from "../styles";
import {
  CustomButton,
  CustomLabel,
  FlexContainer,
  LeftBanner,
  Regex,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import PurchasePriceDescriptionRow from "./PurchasePriceDescriptionRow";
import ItemNotAvailableWarning from "./ItemNotAvailableWarning";
import CustomPricingInput from "./CustomPricingInput";
import CarouselWithThumbnail from "./CarousalWithThumbnail";
import FreeIncludedItems from "./FreeIncludedItems";

type IProps = {
  purchaseStore: PurchaseStore;
  items: CustomerFacingCheckOutItemModel[];
  recommendedItem?: CheckOutRecommendedItemDetails;
  navigate: NavigateFunction;
  onPurchaseClick: (item: CustomerFacingCheckOutItemModel) => void;
  color?: ICheckOutCombinedColors;
  buttonCaption: string;
  buttonStyle: keyof typeof CheckOutButtonStyle;
  updateItems: (items: CustomerFacingCheckOutItemModel[]) => void;
  customOption?: CustomOptionModel;
  allowIncludedItems?: boolean;
};

const SingleItemMultiplePrices: FC<IProps> = ({
  onPurchaseClick,
  items,
  recommendedItem,
  color,
  buttonCaption,
  buttonStyle,
  updateItems,
  customOption,
  allowIncludedItems,
}) => {
  if (!items || items.length === 0) return null;

  function updateCustomPricingInputValue(value: string, index: number) {
    const clonedItems = items.map((item) => {
      return new CustomerFacingCheckOutItemModel(item);
    });

    clonedItems[index].customPricingInputValue = value;
    updateItems(clonedItems);
  }

  const redirect = (customOption: CustomOptionModel) => {
    let redirectUrl = customOption.redirectValue;
    switch (CheckOutRedirectTypes[customOption.redirectType]) {
      case CheckOutRedirectTypes.Website:
        redirectUrl = customOption.redirectValue;
        if (!redirectUrl) return null;
        if (!Regex.httpProtocolRegex.test(redirectUrl)) {
          redirectUrl = "http://" + redirectUrl;
        }
        window.location.href = redirectUrl;
        break;
      case CheckOutRedirectTypes.ClickToEmail:
        if (Regex.validateEmail.test(redirectUrl)) {
          redirectUrl = `mailto:${redirectUrl}`;
        }
        break;
      case CheckOutRedirectTypes.ClickToCall:
        redirectUrl = `tel:${redirectUrl}`;
        break;
      case CheckOutRedirectTypes.ClickToText:
        redirectUrl = `sms:${redirectUrl}`;
        break;
      default:
        return null;
    }
  };

  const item = items[0];
  return (
    <MultiplePriceWrapper>
      <FlexContainer
        spacing={30}
        className="divided-component wrapped-columns wrapped-max-width"
        justifyContent="center"
      >
        <div>
          <MainOuterCard
            className="left-outer-card"
            border={`1px solid ${color?.primary?.rgba}`}
            //  border={`1px solid ${}`} // add border color here
          >
            <CustomLabel
              className="item-header"
              margin="0px 0px 30px"
              color="#000"
            >
              {item.name}
            </CustomLabel>
            {item.descriptionEditorRawContent && (
              <CustomLabel
                margin="0px 0px 40px"
                dangerouslySetInnerHTML={{
                  __html: UtilityMethods.getEditorHtmlContent(
                    item.descriptionEditorRawContent
                  ),
                }}
              ></CustomLabel>
            )}
            <div>
              <CarouselWithThumbnail
                media={item.getAllMedias(true) || []}
                className="option-carousel-wrapper"
              />
            </div>
          </MainOuterCard>
        </div>
        <div>
          <FlexContainer
            justifyContent="center"
            fxLayout="column"
            padding="0px 20px"
            className="item-right-panel"
            spacing={20}
          >
            {items.map((item, index) => {
              const recommendedData =
                recommendedItem?.itemId === item.itemId &&
                recommendedItem.priceId === item.price.id
                  ? recommendedItem
                  : undefined;
              return (
                <MainOuterCard
                  key={index}
                  className={`recommended-outer-card dynamic-labels-card ${!recommendedData ? "no-recommendations" : ""}`}
                  bgColor={color?.primary?.rgba}
                  border={`1px solid ${color?.primary?.rgba}`}
                >
                  <FlexContainer fxLayout="column">
                    <div className="full-width">
                      <FlexContainer justifyContent="space-between">
                        <CustomLabel
                          className="item-header purchase-dark-headers"
                          margin="0px 0px 10px"
                        >
                          {item.price.name}
                        </CustomLabel>
                        <CustomButton
                          bgColor={color?.primary?.rgba}
                          pillShape={buttonStyle === CheckOutButtonStyle.Round}
                          roundedRect={
                            buttonStyle ===
                            CheckOutButtonStyle.SquareRoundCorner
                          }
                          onClick={() =>
                            !item.isItemUnavailable && onPurchaseClick(item)
                          }
                          color={"#fff"}
                          minWidth={"80px"}
                          height="auto"
                          padding="10px 8px"
                          minHeight="30px"
                          disabled={item.isItemUnavailable}
                        >
                          {buttonCaption}
                        </CustomButton>
                       
                      </FlexContainer>
                    </div>
                    <div className="desc-width">
                      {" "}
                      {item.price.description && (
                        <div>
                          <CustomLabel margin="0px 0px 10px">
                            {item.price.description}
                          </CustomLabel>
                        </div>
                      )}
                      {item.isCustomPricing() && (
                        <CustomPricingInput
                          suggestedPrice={item.getCustomPricing()?.preset}
                          hasError={item.hasError("customPricingInputValue")}
                          value={item.customPricingInputValue}
                          onChange={(value) =>
                            updateCustomPricingInputValue(value, index)
                          }
                          errorMessage={item.getError(
                            "customPricingInputValue"
                          )}
                        />
                      )}
                      {!item.isCustomPricing() && (
                        <PurchasePriceDescriptionRow
                          billingType={item.price.billingType}
                          terms={item.price.terms}
                          recurrence={item.price.recurrence}
                          priceId={item.price.id}
                          itemType={item.itemType}
                          hasVariants={item.hasVariants}
                          variants={item.variants}
                          quantity={item.quantity}
                          className="recommend-price-desc"
                        />
                      )}
                      {allowIncludedItems && (
                        <FreeIncludedItems includedItems={item.includedItems} />
                      )}
                    </div>
                  </FlexContainer>
                  {recommendedData && (
                    <LeftBanner bgColor={color?.primary.rgba}>
                      <label>{recommendedData.caption}</label>
                    </LeftBanner>
                  )}
                   {item.isItemUnavailable && <ItemNotAvailableWarning margin="0px" />}
                </MainOuterCard>
              );
            })}
            {customOption && (
              <MainOuterCard
                className={`recommended-outer-card dynamic-labels-card no-recommendations`}
                bgColor={color?.primary?.rgba}
                border={`1px solid ${color?.primary?.rgba}`}
              >
                <FlexContainer fxLayout="column">
                  <div className="full-width">
                    <FlexContainer justifyContent="space-between">
                      <CustomLabel
                        className="item-header purchase-dark-headers"
                        margin="0px 0px 10px"
                      >
                        {customOption.name}
                      </CustomLabel>
                      {customOption.showButton && (
                        <CustomButton
                          bgColor={color?.primary?.rgba}
                          pillShape={buttonStyle === CheckOutButtonStyle.Round}
                          roundedRect={
                            buttonStyle ===
                            CheckOutButtonStyle.SquareRoundCorner
                          }
                          color={"#fff"}
                          minWidth={"80px"}
                          height="auto"
                          onClick={() => redirect(customOption)}
                          padding="10px 8px"
                          minHeight="30px"
                        >
                          {customOption.buttonText}
                        </CustomButton>
                      )}
                    </FlexContainer>
                  </div>
                  <div className="desc-width">
                    {customOption.descriptionEditorRawContent && (
                      <div>
                        <CustomLabel
                          margin="0px 0px 10px"
                          dangerouslySetInnerHTML={{
                            __html: UtilityMethods.getEditorHtmlContent(
                              customOption.descriptionEditorRawContent
                            ),
                          }}
                        ></CustomLabel>
                      </div>
                    )}

                    {customOption.showPrice && (
                      <CustomLabel
                        fontSize={"20px"}
                        margin="20px 0px"
                        fontWeight={"600"}
                        color="black"
                      >
                        {customOption.priceText}
                      </CustomLabel>
                    )}
                  </div>
                </FlexContainer>
              </MainOuterCard>
            )}
          </FlexContainer>
        </div>
      </FlexContainer>
    </MultiplePriceWrapper>
  );
};
export default withRouter(observer(SingleItemMultiplePrices));
